import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import styled from '@emotion/styled';

import SearchIcon from '../../Icons/SearchIcon';

interface ITableSearchBox {
  setSearchQuery: (...args: any[]) => void;
  placeholder?: string;
}

const TableSearchBox: React.FC<ITableSearchBox> = ({
  setSearchQuery,
  placeholder,
}) => {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `?search=` comes from the command menu's "Search … for" items.
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') ?? '');

  useEffect(() => {
    if (searchTerm) setSearchQuery(searchTerm);
    // eslint-disable-next-line
  }, []);

  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setSearchTerm(value);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
      }}
    >
      <InputWrapper>
        <TableSearchInputField
          placeholder={placeholder ?? 'Search table'}
          onChange={handleSearchTermChange}
          value={searchTerm}
          className='input'
          aria-label='Search table records'
        />
        <SearchIconWrapper>
          <SearchIcon />
        </SearchIconWrapper>
      </InputWrapper>
    </Form>
  );
};

export default TableSearchBox;

const Form = styled.form`
  flex: 1;

  @media (max-width: 884px) {
    grid-column: span 4;
    width: 100%;
  }
`;

const TableSearchInputField = styled.input`
  border-radius: 10px;
  padding: 7px 7px 7px 40px;
  text-align: left;
  font-weight: 400;
  line-height: 26px;
  font-size: 14px;
  height: 40px;
  cursor: text;
  width: 360px;
  background: var(--panel);
  border: 1px solid ${({ theme }) => theme.palette.borderColor};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.HetheraPrimary};
  }

  &:hover {
    cursor: text;
  }

  &::placeholder {
    color: #A9A395;
    opacity: 1;
    font-weight: 400;
  }

  @media (max-width: 884px) {
    width: 100%;
  }
`;

const InputWrapper = styled.div`
  display: grid;
  height: fit-content;

  & > * {
    grid-column: 1;
    grid-row: 1;
  }
  &:hover {
    cursor: pointer;
  }

  @media (max-width: 884px) {
    width: 100%;
  }
`;

const SearchIconWrapper = styled.div`
  display: grid;
  place-content: center;
  width: 24px;
  height: 24px;
  align-self: center;
  margin-left: 9px;
  pointer-events: none;
`;
